import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@/core/api";
import type {
  ICellCommentTarget,
  ICellCommentThread,
  ICellCommentThreadResponse,
  ICellCommentThreadsResponse,
} from "./types";

export const getCellCommentThreadsPath = (businessId: string, runId: string) =>
  `/businesses/${businessId}/ai_script_runs/${runId}/cell_comment_threads`;

export const cellCommentThreadsKey = (
  businessId: string | null,
  runId: string | null,
) => ["ai-script-cell-comment-threads", businessId, runId] as const;

export const useCellCommentThreads = (
  businessId: string | null,
  runId: string | null,
  enabled = true,
) => {
  const api = useApi();

  return useQuery({
    queryKey: cellCommentThreadsKey(businessId, runId),
    queryFn: () =>
      api
        .get(getCellCommentThreadsPath(businessId!, runId!))
        .then((res) => (res.data as ICellCommentThreadsResponse).threads),
    enabled: enabled && !!businessId && !!runId,
    retry: false,
  });
};

// Every mutation answers with the updated thread; splice it into the list so
// the grid marker and the open card update without a refetch.
const useThreadMutation = <TVariables>(
  businessId: string | null,
  runId: string,
  request: (
    api: ReturnType<typeof useApi>,
    basePath: string,
    variables: TVariables,
  ) => Promise<{ data: unknown }>,
) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables: TVariables) => {
      if (!businessId) throw new Error("Business is required");

      return request(
        api,
        getCellCommentThreadsPath(businessId, runId),
        variables,
      ).then((res) => (res.data as ICellCommentThreadResponse).thread);
    },
    onSuccess: (thread) => {
      queryClient.setQueryData<ICellCommentThread[]>(
        cellCommentThreadsKey(businessId, runId),
        (current) => upsertThread(current ?? [], thread),
      );
    },
  });
};

export const upsertThread = (
  threads: ICellCommentThread[],
  thread: ICellCommentThread,
) =>
  threads.some((existing) => existing.id === thread.id)
    ? threads.map((existing) => (existing.id === thread.id ? thread : existing))
    : [...threads, thread];

export const useStartCellCommentThread = (
  businessId: string | null,
  runId: string,
) =>
  useThreadMutation(
    businessId,
    runId,
    (
      api,
      basePath,
      {
        target,
        body,
        context,
      }: {
        target: ICellCommentTarget;
        body: string;
        context?: ICellCommentThread["context"];
      },
    ) =>
      api.post(basePath, {
        sheet_id: target.sheetId,
        row: target.row,
        col: target.col,
        body,
        context,
      }),
  );

export const useReplyToCellCommentThread = (
  businessId: string | null,
  runId: string,
) =>
  useThreadMutation(
    businessId,
    runId,
    (api, basePath, { threadId, body }: { threadId: string; body: string }) =>
      api.post(`${basePath}/${threadId}/reply`, { body }),
  );

export const useSetCellCommentThreadResolved = (
  businessId: string | null,
  runId: string,
) =>
  useThreadMutation(
    businessId,
    runId,
    (
      api,
      basePath,
      { threadId, resolved }: { threadId: string; resolved: boolean },
    ) => api.post(`${basePath}/${threadId}/${resolved ? "resolve" : "reopen"}`),
  );

export const useWithdrawCellComment = (
  businessId: string | null,
  runId: string,
) =>
  useThreadMutation(
    businessId,
    runId,
    (
      api,
      basePath,
      { threadId, commentId }: { threadId: string; commentId: string },
    ) => api.delete(`${basePath}/${threadId}/comments/${commentId}`),
  );
